import { isIsoDate } from '../validation/is-iso-date.function';
import { isIsoDateTime } from '../validation/is-iso-date-time.function';
import { isIsoDateTimeTz } from '../validation/is-iso-date-time-tz.function';
import type { IsoDate, IsoDateTime, IsoDateTimeTz } from '../../types/iso-date';
import { deconstructDate } from '../helper/deconstruct-date.function';

/**
 * Returns the week-of-year number (1-53) using Sunday as the first day of the week,
 * where Week 1 is the week containing January 1st.
 *
 * Supported inputs:
 * - Date-only: YYYY-MM-DD
 * - Date-time (no tz): YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?
 * - Date-time with tz: YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?(Z|±HH:MM)
 *
 * The calculation uses UTC to be deterministic across environments. The week is derived
 * from the calendar date portion of the input (time and timezone do not shift the date part).
 */
export function getWeek(input: IsoDate | IsoDateTime | IsoDateTimeTz): number {
  const {year, month, day} = deconstructDate(input);
  if(!year || !month || !day)
    throw new Error('getWeek: invalid ISO date/date-time string');

  const date = new Date(Date.UTC(year, month - 1, day));
  const startOfYear = new Date(Date.UTC(year, 0, 1));

  const millisPerDay = 24 * 60 * 60 * 1000;
  const daysSinceYearStart = Math.floor((date.getTime() - startOfYear.getTime()) / millisPerDay);

  // Sunday=0 .. Saturday=6
  const startDow = startOfYear.getUTCDay();

  // Week number: week containing Jan 1 is week 1; weeks start on Sunday
  return Math.floor((daysSinceYearStart + startDow) / 7) + 1;
}
